import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { StoreApi, UseBoundStore } from "zustand";

import {
  isTypenameValidForView,
  viewSupportsNodePanel,
} from "@/app/app/_/layouts/nodePanelConfig";
import type { IObjectWithTypename, NodeStore } from "@/components/@types";
import { useNode, useNodeSetup } from "@/components/organisms/Node/Node.hooks";
import { createNodeFieldsStore } from "@/components/organisms/Node/Node.store";
import { Queries } from "@/constants";
import { getReportsTabKey } from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { idToTypename } from "@/objects/@utils";
import { State } from "@/state";

export type ActivePanelNodeContextValue = {
  store: UseBoundStore<StoreApi<NodeStore>>;
  isLoading: boolean;
  title: string;
};

const ActivePanelNodeContext: React.Context<ActivePanelNodeContextValue | null> =
  (
    import.meta.hot?.data as {
      ActivePanelNodeContext?: React.Context<ActivePanelNodeContextValue | null>;
    }
  )?.ActivePanelNodeContext ??
  createContext<ActivePanelNodeContextValue | null>(null);

if (import.meta.hot) {
  import.meta.hot.dispose(
    (data: { ActivePanelNodeContext?: typeof ActivePanelNodeContext }) => {
      data.ActivePanelNodeContext = ActivePanelNodeContext;
    },
  );
}

export function useActivePanelNode() {
  return useContext(ActivePanelNodeContext);
}

function ActivePanelNodeLoader({
  nodeId,
  typename,
  onStateChange,
}: {
  nodeId: string;
  typename: string;
  onStateChange: (value: ActivePanelNodeContextValue | null) => void;
}) {
  const storeRef = useRef<UseBoundStore<StoreApi<NodeStore>> | null>(null);
  if (!storeRef.current) {
    const hotStores = import.meta.hot?.data as
      | {
          activePanelNodeStores?: Record<
            string,
            UseBoundStore<StoreApi<NodeStore>>
          >;
        }
      | undefined;
    storeRef.current =
      hotStores?.activePanelNodeStores?.[nodeId] ??
      createNodeFieldsStore("update");
  }
  const store = storeRef.current;

  useEffect(() => {
    if (!import.meta.hot) {
      return;
    }
    const data = import.meta.hot.data as {
      activePanelNodeStores?: Record<
        string,
        UseBoundStore<StoreApi<NodeStore>>
      >;
    };
    data.activePanelNodeStores = {
      ...data.activePanelNodeStores,
      [nodeId]: store,
    };
  }, [nodeId, store]);

  const { data, loading: nodeQueryLoading } = useQuery(
    Queries.NODE_QUERY_WITH_PENDING_CHANGE,
    {
      variables: {
        id: nodeId,
        startDate: State.getState().timeSeriesFilter.startDate?.toISOString(),
        endDate: State.getState().timeSeriesFilter.endDate?.toISOString(),
      },
    },
  );
  const node = data?.node as IObjectWithTypename | undefined;
  const isLoading = nodeQueryLoading && node == null;

  useNodeSetup({
    mode: "update",
    // Its id while the record is in flight, as a record's page seeds its own:
    // what is worked out from the record - which of its tabs it has - knows
    // whose it is before the rest of it lands.
    node: (node ?? {
      id: nodeId,
      __typename: typename,
    }) as IObjectWithTypename,
    typename,
    store,
  });

  const { metadata } = useNode(store);

  useLayoutEffect(() => {
    onStateChange({
      store,
      isLoading,
      title: metadata.title,
    });
  }, [isLoading, metadata.title, onStateChange, store]);

  // Fast Refresh can reset the provider's context state without remounting
  // this loader — republish when the parent hands us a new setter.
  useEffect(() => {
    onStateChange({
      store,
      isLoading,
      title: metadata.title,
    });
  }, [onStateChange, store, isLoading, metadata.title]);

  return null;
}

function ActivePanelNodeContextReset({
  onReset,
}: {
  onReset: (value: ActivePanelNodeContextValue | null) => void;
}) {
  useEffect(() => {
    onReset(null);
  }, [onReset]);
  return null;
}

export function ActivePanelNodeProvider({ children }: { children: ReactNode }) {
  const activeAppView = State((store) => store.activeAppView);
  const activeTab = State((store) => store.activeTab);
  const activePanelNodeId = State((store) => store.activePanelNodeId);
  const activeTabNodeIds = State((store) => store.activeTabNodeIds);
  const tabKey =
    activeAppView === "reports" ? getReportsTabKey(activeTab) : activeTab;
  const detailNodeId = (
    activeTabNodeIds as Record<
      string,
      Record<string, string | null> | undefined
    >
  )?.[activeAppView]?.[tabKey];
  const nodeId = activePanelNodeId ?? detailNodeId ?? null;
  const typename = idToTypename(nodeId);
  const isEligible =
    Boolean(nodeId) &&
    viewSupportsNodePanel(activeAppView) &&
    isTypenameValidForView(activeAppView, typename);

  const [contextValue, setContextValue] =
    useState<ActivePanelNodeContextValue | null>(() => {
      if (!import.meta.hot) {
        return null;
      }
      return (
        (
          import.meta.hot.data as {
            activePanelNodeContextValue?: ActivePanelNodeContextValue | null;
          }
        ).activePanelNodeContextValue ?? null
      );
    });

  useEffect(() => {
    if (!import.meta.hot) {
      return;
    }
    (
      import.meta.hot.data as {
        activePanelNodeContextValue?: ActivePanelNodeContextValue | null;
      }
    ).activePanelNodeContextValue = contextValue;
  }, [contextValue]);

  return (
    <ActivePanelNodeContext.Provider value={contextValue}>
      {isEligible && nodeId && typename ? (
        <ActivePanelNodeLoader
          key={nodeId}
          nodeId={nodeId}
          typename={typename}
          onStateChange={setContextValue}
        />
      ) : (
        <ActivePanelNodeContextReset onReset={setContextValue} />
      )}
      {children}
    </ActivePanelNodeContext.Provider>
  );
}
